import { mappedColors } from '@pixee/design-system'
import { match } from 'ts-pattern'
import { z } from 'zod'
import type { FindingQuickViewId } from '../tables/finding-quick-view-config'
import { getStateColor, type Categorization } from './resolution-categorization.ts'
import {
  ANALYZED_FILTER,
  getAnalyzedTotal,
  RESOLUTION_META,
  toStateFilter,
  TRIAGED_FILTER,
  VALIDATED_FILTER,
  type ResolutionFilter,
  type ResolutionPanelData,
} from './resolution-taxonomy.ts'

/**
 * Which findings the funnel accounts for. `all-findings` wraps the analyzed funnel in an
 * "All findings" root with Unanalyzed and In progress dead-end branches so the totals
 * reconcile; `analyzed-only` roots the funnel at the analyzed set; `unanalyzed` draws no funnel
 * at all, since a view of findings that have not been analyzed has no resolution to break down.
 */
export const FunnelVariantSchema = z.enum(['all-findings', 'analyzed-only', 'unanalyzed'])
export type FunnelVariant = z.infer<typeof FunnelVariantSchema>

/** The variants that draw rows. `unanalyzed` renders a callout in their place. */
export type FunnelRowsVariant = Exclude<FunnelVariant, 'unanalyzed'>

/**
 * The funnel's shape follows the quick view. All findings is the only view that holds unanalyzed
 * and in-progress findings alongside analyzed ones, so it alone draws those branches; Unanalyzed
 * holds nothing analyzed; every other view narrows within the analyzed set.
 */
export const getFunnelVariant = (quickViewId: FindingQuickViewId): FunnelVariant =>
  match(quickViewId)
    .returnType<FunnelVariant>()
    .with('all-findings', () => 'all-findings')
    .with('unanalyzed', () => 'unanalyzed')
    .otherwise(() => 'analyzed-only')

/** Indent levels the funnel can produce: the analyzed funnel is four deep, plus one when nested. */
export type FunnelDepth = 0 | 1 | 2 | 3 | 4 | 5

export type FunnelRow = {
  key: string
  /** Indent level. 0 is the root; each level subdivides its parent. */
  depth: FunnelDepth
  label: string
  description: string
  count: number
  /** The count this row's "% of parent" is measured against. */
  parentCount: number
  color: string
  /**
   * Which side of the flow this row sits on: `out` rows leave the funnel (dead ends),
   * `in` rows carry the remainder down to the next level. Drives the indent guides.
   */
  branch?: 'in' | 'out'
  /** The row whose findings are actively being analyzed — renders a shimmer on its fill. */
  isInProgress?: boolean
  /** Clicking the row filters the table to this set. Rows without one are not clickable. */
  filter?: ResolutionFilter
  /**
   * Clicking the row releases every panel filter, returning the table to the whole quick view. The
   * root row's set is the quick view itself, which no filter expresses.
   */
  clearsFilter?: boolean
}

const AGGREGATE_COLORS = {
  all: mappedColors.neutral.halo,
  unanalyzed: mappedColors.secondary.void,
  inProgress: mappedColors.secondary.haze,
  analyzed: mappedColors.primary.pulse,
  triaged: mappedColors.secondary.prism,
  validated: mappedColors.primary.flare,
  pixeeCanFix: mappedColors.primary.pulse,
}

/**
 * Builds the nested breakdown of how findings flow through Pixee.
 *
 * Every level is a strict partition of its parent: each row's children sum to exactly the row's
 * own count, so no row can exceed 100% of its parent. Validated therefore counts Fixed findings
 * alongside Fix ready and Needs attention — a merged fix was validated first, and `VALIDATED_FILTER`
 * has always selected those findings, since its union of the three states leaves patch status
 * unconstrained. Excluding them from the count alone was what let "Pixee can fix" exceed its parent.
 */
export const buildResolutionFunnelRows = ({
  data,
  variant,
  categorization,
}: {
  data: ResolutionPanelData
  variant: FunnelRowsVariant
  /**
   * Recolours the five leaf rows to the buckets they belong to. Aggregate rows keep their own
   * colours: they stand for stages of the flow rather than for any one bucket, so a bucket colour
   * would misattribute them.
   */
  categorization?: Categorization
}): FunnelRow[] => {
  const { resolution, inProgressCount, unanalyzedCount } = data
  const analyzedTotal = getAnalyzedTotal(resolution)
  const triagedCount = analyzedTotal - resolution.untriaged
  const validatedCount = resolution.fixReady + resolution.acknowledged + resolution.fixed
  const pixeeCanFixCount = resolution.fixReady + resolution.fixed

  const isAllFindings = variant === 'all-findings'
  const rootCount = isAllFindings ? analyzedTotal + inProgressCount + unanalyzedCount : analyzedTotal
  // The analyzed funnel nests one level deeper when the unanalyzed branches sit above it. Spelled
  // out per variant rather than computed, so every depth stays a literal the styles can be keyed by.
  const depths = isAllFindings
    ? ({ level1: 2, level2: 3, level3: 4, level4: 5 } as const)
    : ({ level1: 1, level2: 2, level3: 3, level4: 4 } as const)

  const rootRows: FunnelRow[] = [
    {
      key: 'all',
      depth: 0,
      label: 'All findings',
      description: 'Ingested from scanner',
      count: rootCount,
      parentCount: rootCount,
      color: AGGREGATE_COLORS.all,
      filter: undefined,
      clearsFilter: true,
    },
  ]

  const unanalyzedRows: FunnelRow[] = isAllFindings
    ? [
        {
          key: 'unanalyzed',
          depth: 1,
          label: 'Unanalyzed',
          description: 'Awaiting analysis',
          count: unanalyzedCount,
          parentCount: rootCount,
          color: AGGREGATE_COLORS.unanalyzed,
          branch: 'out',
          // Not clickable, and neither is In progress: the API filters only "fully analyzed" or
          // "not fully analyzed" (`analyzed=false`), which is these two rows together. Neither alone
          // has a filter whose table total would equal its count.
          filter: undefined,
        },
        {
          key: 'inProgress',
          depth: 1,
          label: 'In progress',
          description: 'Analysis running now',
          count: inProgressCount,
          parentCount: rootCount,
          color: AGGREGATE_COLORS.inProgress,
          branch: 'out',
          isInProgress: true,
          filter: undefined,
        },
        {
          key: 'analyzed',
          depth: 1,
          label: 'Analyzed',
          description: 'Analysis complete',
          count: analyzedTotal,
          parentCount: rootCount,
          color: AGGREGATE_COLORS.analyzed,
          branch: 'in',
          filter: ANALYZED_FILTER,
        },
      ]
    : []

  const analyzedRows: FunnelRow[] = [
    {
      key: 'untriaged',
      depth: depths.level1,
      label: RESOLUTION_META.untriaged.label,
      description: 'Review reasons and retry analysis',
      count: resolution.untriaged,
      parentCount: analyzedTotal,
      color: getStateColor({ categorization, state: 'untriaged' }),
      branch: 'out',
      filter: toStateFilter('untriaged'),
    },
    {
      key: 'triaged',
      depth: depths.level1,
      label: TRIAGED_FILTER.label,
      description: 'Classified by Pixee or user',
      count: triagedCount,
      parentCount: analyzedTotal,
      color: AGGREGATE_COLORS.triaged,
      branch: 'in',
      filter: TRIAGED_FILTER,
    },
    {
      key: 'dismissed',
      depth: depths.level2,
      label: RESOLUTION_META.dismissed.label,
      description: 'False positive or won’t fix',
      count: resolution.dismissed,
      parentCount: triagedCount,
      color: getStateColor({ categorization, state: 'dismissed' }),
      branch: 'out',
      filter: toStateFilter('dismissed'),
    },
    {
      key: 'validated',
      depth: depths.level2,
      label: VALIDATED_FILTER.label,
      description: 'Findings that warrant remediation',
      count: validatedCount,
      parentCount: triagedCount,
      color: AGGREGATE_COLORS.validated,
      branch: 'in',
      filter: VALIDATED_FILTER,
    },
    {
      key: 'acknowledged',
      depth: depths.level3,
      label: RESOLUTION_META.acknowledged.label,
      description: 'Blocked / Requires review',
      count: resolution.acknowledged,
      parentCount: validatedCount,
      color: getStateColor({ categorization, state: 'acknowledged' }),
      branch: 'out',
      filter: toStateFilter('acknowledged'),
    },
    {
      key: 'pixeeCanFix',
      depth: depths.level3,
      label: 'Pixee can fix',
      description: 'A fix is ready or already applied',
      count: pixeeCanFixCount,
      parentCount: validatedCount,
      color: AGGREGATE_COLORS.pixeeCanFix,
      branch: 'in',
      filter: undefined,
    },
    {
      key: 'fixReady',
      depth: depths.level4,
      label: `${RESOLUTION_META.fixReady.label} to apply`,
      description: 'Pending your review',
      count: resolution.fixReady,
      parentCount: pixeeCanFixCount,
      color: getStateColor({ categorization, state: 'fixReady' }),
      branch: 'out',
      filter: toStateFilter('fixReady'),
    },
    {
      key: 'fixed',
      depth: depths.level4,
      label: RESOLUTION_META.fixed.label,
      description: 'PR merged. Vulnerability remediated.',
      count: resolution.fixed,
      parentCount: pixeeCanFixCount,
      color: getStateColor({ categorization, state: 'fixed' }),
      branch: 'in',
      filter: toStateFilter('fixed'),
    },
  ]

  return [...rootRows, ...unanalyzedRows, ...analyzedRows]
}
